import { useEffect, useRef } from 'react';
import { reportApi } from './api';

// A section must stay on screen this long to count, which also absorbs
// StrictMode's double effects and readers flicking past tabs.
export const MIN_VIEW_MS = 1000;
const HEARTBEAT_MS = 60000;
const SESSION_KEY = 'cidx.readerSession';

const newId = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`);

const readerSession = () => {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = newId();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return '';
  }
};

const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

/**
 * Records each report section a reader has on screen, and how long it stayed
 * visible, for the admin Report activity panel. `entryRef.current` names how
 * the reader got here (open, product, version or tab); tracking never throws.
 */
export const useReportViewTracking = ({ enabled, ticker, product, tab, versionId, latestVersion, entitled, entryRef }) => {
  const details = useRef({});
  details.current = { tabLabel: tab?.label || '', tabLevel: tab?.level || '', latestVersion, entitled };
  const lastTicker = useRef('');
  const tabId = tab?.id || '';

  useEffect(() => {
    if (!enabled || !ticker || !product) return undefined;
    const entry = lastTicker.current === ticker ? (entryRef?.current || 'tab') : 'open';
    let since = visible() ? Date.now() : null;
    let shownMs = 0;
    let refPromise = null;
    let sentSeconds = -1;
    let timer = null;

    const elapsed = () => shownMs + (since == null ? 0 : Date.now() - since);
    const flush = () => {
      if (!refPromise) return;
      const seconds = Math.floor(elapsed() / 1000);
      if (seconds <= sentSeconds) return;
      sentSeconds = seconds;
      refPromise.then((ref) => ref && reportApi.endView(ref, seconds)).catch(() => {});
    };
    const record = () => {
      timer = null;
      lastTicker.current = ticker;
      if (entryRef) entryRef.current = 'tab';
      const { tabLabel, tabLevel, latestVersion: latest, entitled: canRead } = details.current;
      refPromise = reportApi.recordView({
        ticker,
        product,
        entry,
        view_id: newId(),
        session_id: readerSession(),
        tab_id: tabId,
        tab_label: tabLabel.slice(0, 120),
        tab_level: tabLevel,
        version_id: versionId || '',
        latest_version: latest ?? null,
        entitled: canRead ?? null,
        device: typeof window !== 'undefined' && window.innerWidth < 700 ? 'mobile' : 'desktop'
      }).then((result) => result?.ref || null).catch(() => null);
    };
    const schedule = () => {
      if (refPromise || timer || since == null) return;
      timer = setTimeout(record, Math.max(0, MIN_VIEW_MS - elapsed()));
    };
    const onVisibility = () => {
      if (visible()) {
        if (since == null) since = Date.now();
        schedule();
      } else {
        if (since != null) shownMs += Date.now() - since;
        since = null;
        clearTimeout(timer);
        timer = null;
        flush();
      }
    };
    const heartbeat = setInterval(() => { if (since != null) flush(); }, HEARTBEAT_MS);

    schedule();
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flush);
    return () => {
      clearTimeout(timer);
      clearInterval(heartbeat);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flush);
      flush();
    };
  }, [enabled, entryRef, product, tabId, ticker, versionId]);
};

export default useReportViewTracking;
